import { uniq, uniqBy } from "lodash";
import { DurationUnit } from "~/enums/durationUnit";
import {
  DataPointAggregationType,
  DataPointType,
  DataPointValueFormatType,
  VisualizationType
} from "~/enums/visualizationType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import IRepo from "~/interfaces/repo";
import { ComparisonType } from "~/models/conditionalRules/comparisonType";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import { TargetType } from "~/models/conditionalRules/targetType";
import { DatetimeMode } from "~/models/datetimeMode";
import { VisualizationConditionalField } from "~/models/objectModels/objectModelConditionalFields";
import { RelationType } from "~/models/relationType";
import DataPointReference from "~/models/visualizations/dataPointReference";
import VisualizationDataPoint from "~/models/visualizations/visualizationDataPoint";
import VisualizationDataPointConditionalFields from "~/models/visualizations/visualizationDataPointConditionalFields";
import VisualizationDataPointConditionalRuleSet from "~/models/visualizations/visualizationDataPointConditionalRuleSet";
import { IVisualizationDataPoint } from "~/models/visualizations/visualizationInterfaces";
import VisualizationSource from "~/models/visualizations/visualizationSource";
import VisualizationSourceDataPoint from "~/models/visualizations/visualizationSourceDataPoint";
import VisualizationSourceForm from "~/models/visualizations/visualizationSourceForm";
import { IPeekerOptions } from "~/services/peeker";
import Repo from "~/state/modules/repo";
import store from "~/state/store";
import Form from "../../../formability/form_builder/models/form";
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import { DataPointModel } from "../../../formability/visualizationBuilder/enums/builder-area";
import { StaticDateDataPoints } from "../../../formability/visualizationBuilder/enums/data-point-static-type";
import Selected from "../../../formability/visualizationBuilder/store/modules/selected";
import Visualization from "./visualization";
import VisualizationRange from "./visualizationRange";

interface ICustomizeDisplayOption {
  title: string;
  client_id: string;
}

export default class VisualizationDataFusionPoint extends VisualizationDataPoint implements IVisualizationDataPoint {
  readonly friendly_type = DataPointModel.dataFusion;
  header: string;
  icon = "";

  title: string;
  data_point_references: DataPointReference[] = [];
  data_point_type = DataPointType.Fusion;

  is_filtered: boolean;
  customize_display: boolean;
  enable_total: boolean;

  temp_selected_customize_display_options = [];

  isValidForConditionalLogic: boolean;
  referenceNameOrDefault: string;
  referenced_row_client_id: string;
  referenced_col_client_id: string;

  datetime_mode: DatetimeMode;

  data_point_conditional_rule_sets: Hash<VisualizationDataPointConditionalFields>;

  _nameInParent = "data_fusion_points";
  rootModel: Visualization;

  except = ['data_point_references'];

  constructor(fusion_json, rootVisualization: Visualization) {
    super();
    this.defineProperty('is_filtered', false);
    this.defineProperty('customize_display', false);
    this.defineProperty('enable_total', true)
    this.defineProperty('units_of_time', fusion_json.units_of_time || DurationUnit.minute)
    this.defineProperty("title", fusion_json.title)
    this.defineProperty("data_point_type", DataPointType.Fusion);
    this.defineProperty("referenced_row_client_id", fusion_json.referenced_row_client_id || '')
    this.defineProperty("referenced_col_client_id", fusion_json.referenced_col_client_id || '')
    this.defineProperty('missing_data', fusion_json.missing_data || false);

    this.referenceNameOrDefault = fusion_json.title;
    this.isValidForConditionalLogic = true;
    this.rootModel = rootVisualization || Selected.visualization.get();

    this.data_point_references = fusion_json.data_point_references ? fusion_json.data_point_references.map(ref => new DataPointReference(ref.reference_id, ref.visualization_source_id, this.rootModel)) : [];

    const customizeOptions = Object.assign([], {parent: this, key: 'selected_customize_display_options'});
    this.defineProperty('selected_customize_display_options', TrackedArrayProxy.create(customizeOptions));

    this.defineProperty('aggregation_type',  DataPointAggregationType.Count)
    this.defineProperty('value_format_type', DataPointValueFormatType.Number)
    
    for (const definition of VisualizationDataPointConditionalFields.VISUALIZATION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }

    this.addToRepo();
  }

  createEmbeddedObject = {
    ranges: (parent, json) => {      
      const obj = new VisualizationRange(parent, json, this.rootModel)
      return obj;
    }
  }

  get TRACKED_ARRAYS(): string[] {
    return [
      'selected_customize_display_options',
    ];
  }

  static repo: IRepo<VisualizationDataFusionPoint> = new Repo<VisualizationDataFusionPoint>('visualizationDataFusionPoints', VisualizationDataFusionPoint, {});
  repo: IRepo<this>;

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'data_point_references': { type: DataPointReference, relationType: RelationType.array },
      'ranges': { type: VisualizationRange, relationType: RelationType.dictionary },
    };

    for (const definition of VisualizationDataPointConditionalFields.VISUALIZATION_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: VisualizationDataPointConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }

    return models;
  }

  addToRepo(): void {
    VisualizationDataFusionPoint.repo.add(this);
  }

  get dataPointReferencesArray(): DataPointReference[] {
    return this.data_point_references;
  }

  get source() {
    return null;
  }

  get sources() {
    return this.dataPointReferencesArray.map(ref => ref.referencedDataPoint.source);
  }

  get sourceElement() {
    return null;
  }

  get type() {
    return this.friendly_type
  }

  get allWorkflowSteps() {
    return (this.sources as Form[]).reduce((arr, s) => arr.concat(s.workflowStepsArray), []);
  }

  get workflowStepCompleters() {
    return (this.visualizationSources as VisualizationSourceForm[]).reduce((arr, s) => arr.concat(s.workflowStepCompleters) ,[]);
  }

  get assignedToCompleters() {
    return (this.visualizationSources as VisualizationSourceForm[]).reduce((arr, s) => arr.concat(s.assignToCompleters) ,[]);
  }

  get allStatuses() {
    return this.dataPointReferencesArray.reduce((arr, ref) => arr.concat(ref.referencedDataPoint.allStatuses), []);
  }

  get isPickList() {
    return this.dataPointReferencesArray.some(point => point.referencedDataPoint.isPickList);
  }

  get isCurrency() {
    return this.dataPointReferencesArray.some(point => point.referencedDataPoint.isCurrency);
  }

  get currencyType() {
    return this.dataPointReferencesArray.find(point => point.referencedDataPoint.isCurrency).referencedDataPoint.currencyType;
  }

  get visualizationSource(): VisualizationSource {
    return null;
  }

  get visualizationSources(): VisualizationSource[] {
    return this.dataPointReferencesArray.map(ref => ref.referencedDataPoint.visualizationSource);
  }

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  get decimalPlaces() {
    return Math.min(...this.dataPointReferencesArray.map(ref => ref.referencedDataPoint.decimalPlaces));
  }

  get originalTitle() {
    const dataPoint = this.rootModel.dataFusionPointsArray.find(dp => {
      return this.dataPointReferencesArray.every(ref => dp.dataPointReferencesArray.some(innerRef => innerRef.reference_id === ref.reference_id));
    });

    if (dataPoint) {
      return dataPoint.title;
    }

    return "";
  }

  isInTable(point) {
    return point.dataPointReferencesArray && this.dataPointReferencesArray.length === point.dataPointReferencesArray.length && this.dataPointReferencesArray.every((ref, index) => {
      const otherRef = point.dataPointReferencesArray[index];
      return ref.reference_id === otherRef.reference_id && ref.visualization_source_id === otherRef.visualization_source_id;
    });
  }

  setRanges(ranges: VisualizationRange[]) {
    const rangesObj = ranges.reduce((obj, r) => {
      r._nameInParent = 'ranges';
      // r.rootModel = this;
      r.parent = this;
      this.trackCreationDeletion(r.client_id, null, r.asJson(), r);
      obj[r.client_id] = r;
      return obj;
    }, {})

    Object.values(this.ranges).forEach(r => {
      if (!rangesObj[r.client_id]) {
        r.destroy();
      }
    })

    this.ranges = rangesObj;
    this.parent.saveChangesToStore();
  }

  // ******************** DISPLAY HELPERS

  get isNumberOfRecordsOrSubmissions(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.isNumberOfRecordsOrSubmissions);
  }

  get isValid(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.isValid);
  }

  get isPublished(): boolean {
    return !this.dataPointReferencesArray.some(ref => !ref.referencedVisualizationSource.isPublished);
  }

  get isAccountRelationship(): boolean {
    return !this.dataPointReferencesArray.some(ref => !ref.referencedDataPoint.isAccountRelationship);
  }

  get sourcesContainsDataPoint(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.sourcesContainsDataPoint);
  }

  get elementType() {
    return this.dataPointReferencesArray.length ? this.dataPointReferencesArray[0].referencedDataPoint.elementType : null;
  }

  get shouldShowFormat(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.shouldShowFormat);
  }

  get shouldShowQueryFor(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.shouldShowQueryFor);
  }

  get shouldShowUnitsOfTime(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.shouldShowUnitsOfTime);
  }

  get shouldShowCustomizeDisplay(): boolean {
    return !this.dataPointReferencesArray.some(ref => !ref.referencedDataPoint.shouldShowCustomizeDisplay);
  }

  get shouldShowSetRange(): boolean {
    return (this.isRow || this.isColumn) && this.dataPointReferencesArray.some(r => {
      return r.referencedDataPoint.data_point_type === DataPointType.Measure || r.referencedDataPoint.isDateTimeType
    });
  }

  get shouldShowInclude(): boolean {
    return this.rootModel.visualization_type === VisualizationType.PivotTable && (this.isRow || this.isColumn);
  }

  get customizeDisplayOptions(): ICustomizeDisplayOption[] {
    if (this.isAccountRelationship) {
      return this.selected_customize_display_options.filter(o => o !== "").map(o => ({title: o, client_id: o}));
    }

    if (this.dataPointReferencesArray[0].referencedDataPoint.data_point_type === DataPointType.Measure || this.isDateTimeType || this.staticLabels(StaticDateDataPoints).includes(this.dataPointReferencesArray[0].referencedDataPoint.referenced_client_id)) {
      return this.measureBuckets().map(b => ({title: b, client_id: b}));
    }

    return uniqBy(this.dataPointReferencesArray.reduce((agg, ref) => {
      agg = agg.concat(ref.referencedDataPoint.customizeDisplayOptions);

      return agg;
    }, []), 'client_id');
  }

  get customizeDisplayOptionsMap() {
    const tracker = {};
    return this.customizeDisplayOptions.reduce((obj, value) => {
      if (!tracker[value.client_id]) {
        obj[value.client_id] = value;
        tracker[value.client_id] = true;
      }

      return obj;
    }, {});
  }

  get selectedDisplayOptions() {
    if (!this.customize_display) {
      return this.customizeDisplayOptions;
    }

    if (this.isAccountRelationship) {
      return this.customizeDisplayOptions.filter(o => this.selected_customize_display_options.includes(o.client_id));
    }

    return this.customizeDisplayOptions.filter(option => !this.selected_customize_display_options.includes(option.client_id));
  }

  get isMeasure() {
    return this.dataPointReferencesArray[0].referencedDataPoint.data_point_type === DataPointType.Measure;
  }

  get shouldDropValue(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.shouldDropValue)
  }

  getDataHeaders(): string[] {
    const h = uniq(this.selectedDisplayOptions.map(c => c.title));
    
    if (!h.length) {
      return [""]
    }

    return h;
  }

  // ******************** TRACK CHANGES

  get storeName(): string {
    return `visualizationStore`;
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = { area: this.currentBuilderArea(), modelId: this.clientIdOrId }): void {
    store.commit(`${this.storeName}/saveChanges`, { changes, peekerOptions });
  }

  getConditionalRuleSets(fieldName: string): Hash<VisualizationDataPointConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): VisualizationDataPointConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: VisualizationConditionalField): VisualizationDataPointConditionalRuleSet {
    return new VisualizationDataPointConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): VisualizationSourceDataPoint | VisualizationDataFusionPoint {
    let dataPoint: VisualizationSourceDataPoint | VisualizationDataFusionPoint = VisualizationSourceDataPoint.repo.findBy(d => d.referenced_client_id === id);

    if (!dataPoint) {
      dataPoint = VisualizationDataFusionPoint.repo.findBy(dp => dp.client_id === this.client_id);
    }

    return dataPoint;
  }

  getReferencableControls(): VisualizationSourceDataPoint[] {
    return this.dataPointReferencesArray.reduce((agg, ref) => {
      agg = agg.concat(ref.referencedDataPoint.getReferencableControls());
      return agg;
    }, []);
  }

  displayConditionType(targetType: TargetType): ComparisonType {
    return this.dataPointReferencesArray.length ? this.dataPointReferencesArray[0].referencedDataPoint.displayConditionType(targetType) : ComparisonType.text;
  }

  getChoiceComparisonOptions(): any[] {
    return this.dataPointReferencesArray.length ? this.dataPointReferencesArray[0].referencedDataPoint.getChoiceComparisonOptions() : [];
  }

  getConditionDateTimeMode(): DatetimeMode {
    return DatetimeMode.Datetime;
  }

  getConditionMinDateTime(): string {
    return '';
  }

  getConditionMaxDateTime(): string {
    return '';
  }

  targetTypes(): TargetType[] {
    return [];
  }

  isFusedWith(dataPoint): boolean {
    // return true;
    if (!dataPoint.source) {
      return this.dataPointReferencesArray.some(ref => {
        return dataPoint.dataPointReferencesArray.some(ref2 => ref.visualization_source_id === ref2.visualization_source_id);
      });
    }
    
    return this.dataPointReferencesArray.some(ref => ref.visualization_source_id === dataPoint.visualizationSource?.client_id);
  }

  get isDateTimeType(): boolean {
    return this.dataPointReferencesArray.some(ref => ref.referencedDataPoint.isDateTimeType);
  }

  // ******************** TRACK CHANGES END

}
